import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "../VipServices/vip-services.css";
import { Helmet } from "react-helmet-async";
import Footer from "../../../customComponents/Footer";
import AuthService from "../../../api/services/AuthService";

const VOLUME_BANDS = [
  { id: "Regular", min: 0, nextMin: 50000, volume: "< 50,000", img: "/images/vip-services-img/vip_tag_img.svg", vectorImg: "/images/vip-services-img/vip_table_vector.svg", benifitsImg: "/images/vip-services-img/benifits_btn_icon.svg", btnClass: "one" },
  { id: "VIP 1", min: 50000, nextMin: 250000, volume: "50,000 – 249,999", img: "/images/vip-services-img/vip_tag_img2.svg", vectorImg: "/images/vip-services-img/vip_table_vector2.svg", benifitsImg: "/images/vip-services-img/benifits_btn_icon2.svg", btnClass: "two" },
  { id: "VIP 2", min: 250000, nextMin: 1000000, volume: "250,000 – 999,999", img: "/images/vip-services-img/vip_tag_img3.svg", vectorImg: "/images/vip-services-img/vip_table_vector3.svg", benifitsImg: "/images/vip-services-img/benifits_btn_icon3.svg", btnClass: "three" },
  { id: "VIP 3", min: 1000000, nextMin: 5000000, volume: "1,000,000 – 4,999,999", img: "/images/vip-services-img/vip_tag_img4.svg", vectorImg: "/images/vip-services-img/vip_table_vector4.svg", benifitsImg: "/images/vip-services-img/benifits_btn_icon4.svg", btnClass: "four" },
  { id: "VIP 4", min: 5000000, nextMin: 25000000, volume: "5,000,000 – 24,999,999", img: "/images/vip-services-img/vip_tag_img5.svg", vectorImg: "/images/vip-services-img/vip_table_vector5.svg", benifitsImg: "/images/vip-services-img/benifits_btn_icon5.svg", btnClass: "five" },
  { id: "VIP 5", min: 25000000, nextMin: null, volume: "≥ 25,000,000", img: "/images/vip-services-img/vip_tag_img6.svg", vectorImg: "/images/vip-services-img/vip_table_vector5.svg", benifitsImg: "/images/vip-services-img/benifits_btn_icon5.svg", btnClass: "six" },
];

const MARKETS = [
  {
    key: "spot",
    label: "Spot & margin",
    note: null,
    rates: [
      { makerFee: "0.120%", takerFee: "0.150%", benefits: "Standard rate" },
      { makerFee: "0.100%", takerFee: "0.130%", benefits: "0.020% lower" },
      { makerFee: "0.100%", takerFee: "0.115%", benefits: "0.035% lower" },
      { makerFee: "0.100%", takerFee: "0.108%", benefits: "0.042% lower" },
      { makerFee: "0.100%", takerFee: "0.104%", benefits: "0.046% lower" },
      { makerFee: "0.100%", takerFee: "0.100%", benefits: "0.050% lower" },
    ],
  },
  {
    key: "futures",
    label: "Futures",
    note: null,
    rates: [
      { makerFee: "0.025%", takerFee: "0.058%", benefits: "Standard rate" },
      { makerFee: "0.023%", takerFee: "0.055%", benefits: "0.003% lower" },
      { makerFee: "0.022%", takerFee: "0.053%", benefits: "0.005% lower" },
      { makerFee: "0.021%", takerFee: "0.052%", benefits: "0.006% lower" },
      { makerFee: "0.0205%", takerFee: "0.051%", benefits: "0.007% lower" },
      { makerFee: "0.020%", takerFee: "0.050%", benefits: "0.008% lower" },
    ],
  },
  {
    key: "options",
    label: "Options",
    note: null,
    rates: [
      { makerFee: "0.030%", takerFee: "0.038%", benefits: "Standard rate" },
      { makerFee: "0.030%", takerFee: "0.034%", benefits: "0.004% lower" },
      { makerFee: "0.030%", takerFee: "0.032%", benefits: "0.006% lower" },
      { makerFee: "0.030%", takerFee: "0.031%", benefits: "0.007% lower" },
      { makerFee: "0.030%", takerFee: "0.030%", benefits: "0.008% lower" },
      { makerFee: "0.030%", takerFee: "0.030%", benefits: "0.008% lower" },
    ],
  },
];

function hasSession() {
  try {
    return !!(localStorage.getItem("token") || sessionStorage.getItem("token"));
  } catch {
    return false;
  }
}

function parseAmount(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

/** API fees are already percents: 0.1 → 0.100%, 0.115 → 0.115%. */
function formatFeePercent(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  const four = n.toFixed(4);
  return `${four.endsWith("0") ? n.toFixed(3) : four}%`;
}

function formatVolumeNumber(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("en-US", { maximumFractionDigits: 2 });
}

function formatUsdt(value) {
  return `${formatVolumeNumber(value)} USDT`;
}

function sortedVipTiers(tiers) {
  return [...(tiers || [])]
    .filter((tier) => tier && tier.enabled !== false)
    .sort((a, b) => Number(a.sort) - Number(b.sort));
}

function tiersForMarket(market) {
  return VOLUME_BANDS.map((band, index) => ({
    ...band,
    ...market.rates[index],
  }));
}

/** Futures fees are fractions: 0.0002 → 0.02%. Spot fees are already percents. */
function formatFractionFeePercent(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  const text = (n * 100).toFixed(8).replace(/\.?0+$/, "");
  return `${text || "0"}%`;
}

function rowsFromApi(status, formatFee) {
  const vip = sortedVipTiers(status?.tiers);
  if (!vip.length) return null;
  const regularMaker = Number(status?.regular_maker_fee);
  const regularTaker = Number(status?.regular_taker_fee);
  const firstMin = parseAmount(vip[0].min_volume_usdt);
  const regular = {
    ...VOLUME_BANDS[0],
    id: "Regular",
    code: null,
    min: 0,
    nextMin: firstMin,
    volume: `< ${formatVolumeNumber(firstMin)}`,
    makerFee: formatFee(regularMaker),
    takerFee: formatFee(regularTaker),
    benefits: "Standard rate",
  };
  const rows = vip.map((tier, index) => {
    const style = VOLUME_BANDS[Math.min(index + 1, VOLUME_BANDS.length - 1)];
    const next = vip[index + 1];
    const min = parseAmount(tier.min_volume_usdt);
    const nextMin = next ? parseAmount(next.min_volume_usdt) : null;
    const upper = nextMin == null ? null : (Number.isInteger(nextMin) ? nextMin - 1 : nextMin);
    const rebate = regularTaker - Number(tier.taker_fee);
    return {
      ...style,
      id: tier.label || tier.code,
      code: tier.code,
      min,
      nextMin,
      volume: upper == null
        ? `≥ ${formatVolumeNumber(min)}`
        : `${formatVolumeNumber(min)} – ${formatVolumeNumber(upper)}`,
      makerFee: formatFee(tier.maker_fee),
      takerFee: formatFee(tier.taker_fee),
      benefits: rebate > 0 ? `Save ${formatFee(rebate)}` : "VIP rate",
    };
  });
  return [regular, ...rows];
}

function spotRowsFromApi(status) {
  return rowsFromApi(status, formatFeePercent);
}

function futuresRowsFromApi(status) {
  return rowsFromApi(status, formatFractionFeePercent);
}

function optionsRowsFromApi(status) {
  return rowsFromApi(status, formatFeePercent);
}

function progressFromStatus(status) {
  const vip = sortedVipTiers(status?.tiers);
  const hasSchedule = vip.length > 0;
  const volume = parseAmount(status?.volume_usdt_30d);
  const remaining = parseAmount(status?.volume_remaining_usdt);
  const saved = status?.tier ? vip.find((tier) => tier.code === status.tier) : null;
  const currentMin = saved ? parseAmount(saved.min_volume_usdt) : 0;
  const next = status?.next_tier ? vip.find((tier) => tier.code === status.next_tier) : null;
  const nextMin = next ? parseAmount(next.min_volume_usdt) : null;
  const atTop = hasSchedule && !status?.next_tier;
  let percent = atTop ? 100 : 0;
  if (!atTop && nextMin != null) {
    const span = nextMin - currentMin;
    percent = span > 0 ? Math.min(100, Math.max(0, ((volume - currentMin) / span) * 100)) : 0;
  }
  return {
    label: status?.tier_label || "Regular",
    volume,
    nextLabel: status?.next_tier_label,
    nextMin,
    percent,
    remaining,
    atTop,
    hasSchedule,
    qualifies: !atTop && hasSchedule && remaining <= 0 && Boolean(status?.next_tier),
  };
}

function isYourTier(tier, status, marketKey) {
  if (!status || (marketKey !== "spot" && marketKey !== "futures" && marketKey !== "options")) return false;
  if (tier.code && status.tier) return tier.code === status.tier;
  if (!status.tier) return tier.id === "Regular";
  return tier.id === status.tier_label;
}

function VipVolumeCard({ title, progress, loading, signedIn, error, ariaLabel }) {
  return (
    <article className="vip_progress_card">
      <div className="vip_progress_head">
        <span className="vip_progress_market">{title}</span>
        {progress && <span className="vip_progress_tier">{progress.label}</span>}
      </div>
      {loading && <p className="vip_progress_remain">Loading your VIP progress…</p>}
      {!loading && !signedIn && (
        <>
          <p className="vip_progress_remain">Log in to view your 30-day volume and your progress to the next VIP tier.</p>
          <Link to="/login" className="vip_progress_login">Log in <i class="ri-arrow-right-line"></i></Link>
        </>
      )}
      {!loading && signedIn && error && !progress && (
        <p className="vip_progress_remain">{error}</p>
      )}
      {progress && (
        <>
          <div
            className="vip_progress_track"
            role="progressbar"
            aria-valuenow={Math.round(progress.percent)}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={ariaLabel}
          >
            <div className="vip_progress_fill" style={{ width: `${progress.percent}%` }} />
          </div>
          <div className="vip_progress_scale">
            <span>{formatVolumeNumber(progress.volume)}</span>
            <span>{progress.nextMin == null ? (progress.atTop ? "Top tier" : "") : formatVolumeNumber(progress.nextMin)}</span>
          </div>
          {progress.hasSchedule && (
            <p className="vip_progress_remain">
              {progress.atTop
                ? "You have reached our top VIP tier"
                : progress.qualifies
                  ? `You have qualified for ${progress.nextLabel}. Your upgrade will be applied shortly.`
                  : `Trade ${formatUsdt(progress.remaining)} more to reach ${progress.nextLabel}`}
            </p>
          )}
        </>
      )}
    </article>
  );
}

const VipServices = () => {
  const [marketKey, setMarketKey] = useState("spot");
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [hoveredCardId, setHoveredCardId] = useState(null);
  const [vipStatus, setVipStatus] = useState(null);
  const [futuresStatus, setFuturesStatus] = useState(null);
  const [optionsStatus, setOptionsStatus] = useState(null);
  const [vipLoading, setVipLoading] = useState(() => hasSession());
  const [vipError, setVipError] = useState("");
  const [futuresError, setFuturesError] = useState("");
  const [optionsError, setOptionsError] = useState("");
  const signedIn = hasSession();
  const market = MARKETS.find((item) => item.key === marketKey) || MARKETS[0];
  const apiSpotRows = spotRowsFromApi(vipStatus);
  const apiFuturesRows = futuresRowsFromApi(futuresStatus);
  const apiOptionsRows = optionsRowsFromApi(optionsStatus);
  const vipTiers = market.key === "spot" && apiSpotRows
    ? apiSpotRows
    : market.key === "futures" && apiFuturesRows
      ? apiFuturesRows
      : market.key === "options" && apiOptionsRows
        ? apiOptionsRows
        : tiersForMarket(market);
  const spotProgress = vipStatus ? progressFromStatus(vipStatus) : null;
  const futuresProgress = futuresStatus ? progressFromStatus(futuresStatus) : null;
  const optionsProgress = optionsStatus ? progressFromStatus(optionsStatus) : null;
  const activeStatus = market.key === "futures"
    ? futuresStatus
    : market.key === "options"
      ? optionsStatus
      : market.key === "spot"
        ? vipStatus
        : null;

  useEffect(() => {
    if (!hasSession()) {
      setVipLoading(false);
      return undefined;
    }
    let cancelled = false;
    setVipLoading(true);
    const applyStatus = (res, setStatus, setError) => {
      if (cancelled) return;
      if (res?.success && res.data && !Array.isArray(res.data)) {
        setStatus(res.data);
        setError("");
      } else {
        setError(res?.message || "We couldn't load your VIP status right now. Please try again later.");
      }
    };
    Promise.all([
      AuthService.getMyVip("spot").then((res) => applyStatus(res, setVipStatus, setVipError)),
      AuthService.getMyVip("futures").then((res) => applyStatus(res, setFuturesStatus, setFuturesError)),
      AuthService.getMyVip("options").then((res) => applyStatus(res, setOptionsStatus, setOptionsError)),
    ])
      .catch(() => {
        if (!cancelled) {
          setVipError("We couldn't load your VIP status right now. Please try again later.");
          setFuturesError("We couldn't load your VIP status right now. Please try again later.");
          setOptionsError("We couldn't load your VIP status right now. Please try again later.");
        }
      })
      .finally(() => {
        if (!cancelled) setVipLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>VIP Program | AGCX</title>
        <meta name="description" content="Join the AGCX VIP Program for lower trading fees, priority support and exclusive benefits. Move up the tiers automatically with your 30-day trading volume." />
        <meta
          name="keywords"
          content="AGCX VIP program, VIP benefits, priority support, lower trading fees, spot futures options fees"
        />
        <link rel="canonical" href="https://arabglobal.ae/vip-services" />
      </Helmet>

      <div className="vip_hero_section">
        <div className="container">
          <div className="vip_cnt_mdp">
            <div className="vip_hero_img">
              <img src="/images/vip-services-img/vip_hero_img.png" alt="vip" />
            </div>

            <h1>AGCX VIP Program</h1>
            <p>Trade more, unlock more. Move up the VIP tiers to enjoy lower trading fees, priority support and exclusive member benefits.</p>
            <div className="vip d-flex justify-content-center gap-3 flex-wrap">
              <Link to="/contact" className="vip-hero-secondary">Get in Touch</Link>
              <a href="#vip-tiers" className="applybtn">Explore VIP Tiers</a>
              <Link to="/contact" className="vip-hero-secondary">Contact Us</Link>
            </div>

          </div>
        </div>
      </div>

      <div className="vip_tier_section" id="vip-tiers">
        <div className="container">
          <div className="vip_tier_content">
            <div className="vip_tier_title_container">
              <h2 className="vip_tier_title">VIP Tiers &amp; Fees</h2>
              <p className="vip_tier_lead">Your tier is based on your trading volume over the last 30 days. Spot, isolated margin and cross margin count together, while Futures and Options have their own tiers.</p>
            </div>

            <div className="vip_progress_grid">
              <VipVolumeCard
                title="Spot & margin"
                progress={spotProgress}
                loading={vipLoading}
                signedIn={signedIn}
                error={vipError}
                ariaLabel="Spot and margin progress to the next VIP tier"
              />
              <VipVolumeCard
                title="Futures"
                progress={futuresProgress}
                loading={vipLoading}
                signedIn={signedIn}
                error={futuresError}
                ariaLabel="Futures progress to the next VIP tier"
              />
              <VipVolumeCard
                title="Options"
                progress={optionsProgress}
                loading={vipLoading}
                signedIn={signedIn}
                error={optionsError}
                ariaLabel="Options progress to the next VIP tier"
              />
            </div>

            <div className="vip_market_tabs" role="tablist" aria-label="Fee schedule market">
              {MARKETS.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  role="tab"
                  aria-selected={item.key === market.key}
                  className={`vip_market_tab${item.key === market.key ? " is-active" : ""}`}
                  onClick={() => setMarketKey(item.key)}
                >
                  {item.label}
                </button>
              ))}
            </div>

            <div className="vx_tier_grid">
              {vipTiers.map((tier) => {
                const tierKey = tier.code || tier.id;
                const isMine = isYourTier(tier, activeStatus, market.key);
                const isMe = isMine || selectedCardId === tierKey || hoveredCardId === tierKey;
                return (
                  <article
                    key={tierKey}
                    className={`vx_tier_card vx_c_${tier.btnClass}${isMe ? " is-me" : ""}`}
                    onClick={() => setSelectedCardId(selectedCardId === tierKey ? null : tierKey)}
                    onMouseEnter={() => setHoveredCardId(tierKey)}
                    onMouseLeave={() => setHoveredCardId(null)}
                  >
                    {isMine && <span className="vx_tier_tag">Your tier</span>}
                    <div className="vx_tier_head">
                      <span className="vx_tier_hex"><img src={tier.img} alt="" /></span>
                      <h3 className="vx_tier_name">{tier.id}</h3>
                    </div>
                    <p className="vx_tier_vol">30-day volume (USDT)<strong>{tier.volume}</strong></p>
                    <div className="vx_tier_fees">
                      <div className="vx_tier_fee"><span>Maker fee</span><strong>{tier.makerFee}</strong></div>
                      <div className="vx_tier_fee"><span>Taker fee</span><strong>{tier.takerFee}</strong></div>
                    </div>
                    <p className="vx_tier_save">{tier.benefits}</p>
                  </article>
                );
              })}
            </div>
            {market.note && <p className="vip_market_note">{market.note}</p>}
          </div>
        </div>
      </div>

      {/* <div className="vip_prime_section">
        <div className="container">
          <div className="vip_prime_title_container">
            <img className="vip_heading_vector" src="/images/vip-services-img/vip_prime_vector.png" alt="vip_prime_vector" />
            <h2 className="vip_prime_title">VIP Prime</h2>
          </div>

          <div className="vip_prime_cards_container">

            <div className="vip_prime_card">
              <div className="vip_prime_card_left">
                <div className="coin_info">
                  <img src="/images/vip-services-img/usdt_coin.svg" className="coin_icon" alt="USDT" />
                  <span className="coin_name">USDT</span>
                </div>
                <div className="metrics_container">
                  <div className="metric_col">
                    <span className="metric_label">Reference APR</span>
                    <span className="metric_value">
                      4.5% <span className="gold_badge">+ 26.66%</span>
                    </span>
                  </div>
                  <div className="metric_col">
                    <span className="metric_label">Term</span>
                    <span className="metric_value">
                      365 <span className="metric_unit">days</span>
                    </span>
                  </div>
                </div>
              </div>
              <div className="vip_prime_card_right">
                <img className="vip_right_vector" src="/images/vip-services-img/vip_cnt_card_vector.png" alt="vip_right_vector" />
                <div className="right_title_row">
                  <span className="right_title">Earning Prime</span>
                  <span className="vip_pill_badge">VIP</span>
                </div>
                <div className="right_metric">
                  <span className="right_label">APR After Bonus</span>
                  <span className="right_value">+ 5.7%</span>
                </div>
                <button className="join_btn">Join Now</button>
              </div>
            </div>

           
            <div className="vip_prime_card gempoolprime">
              <div className="vip_prime_card_left">
                <div className="coin_info">
                  <img src="/images/vip-services-img/agce_coin.svg" className="coin_icon" alt="AGCX" />
                  <span className="coin_name">AGCX<span className="coin_subname">-USDT Pool</span></span>
                  <span className="coin_desc">Lock USDT, earn KCS</span>
                </div>
                <div className="metrics_container">
                  <div className="metric_col">
                    <span className="metric_label">Prize Pool (KCS)</span>
                    <span className="metric_value">100,000</span>
                  </div>
                  <div className="metric_col">
                    <span className="metric_label">Reference APR</span>
                    <span className="metric_value">69.75%</span>
                  </div>
                </div>
                <div className="special_boost_text">
                  Special Lock-Up Boost for VIPs
                </div>
              </div>
              <div className="vip_prime_card_right">
                <img className="vip_right_vector" src="/images/vip-services-img/vip_cnt_card_vector2.png" alt="vip_right_vector" />
                <div className="right_title_row">
                  <span className="right_title">GemPool Prime</span>
                  <span className="vip_pill_badge">VIP</span>
                </div>
                <div className="right_metric">
                  <span className="right_label">Max Lock-Up Boost</span>
                  <span className="right_value"><span className="arrow_up"><i class="ri-arrow-up-line"></i></span> 50%</span>
                </div>
                <button className="join_btn">Join Now</button>
              </div>
            </div>

            
            <div className="vip_prime_card mining_prime">
              <div className="vip_prime_card_left">
                <div className="coin_info">
                  <img src="/images/vip-services-img/btc_coin.svg" className="coin_icon" alt="BTC" />
                  <span className="coin_name">BTC</span>
                </div>
                <div className="metrics_container">
                  <div className="metric_col">
                    <span className="metric_label">Hashrate</span>
                    <span className="metric_value">
                      13,631 TH <span className="gold_badge">Save 100 USDT with VIP</span>
                    </span>
                  </div>
                  <div className="metric_col">
                    <span className="metric_label">Period</span>
                    <span className="metric_value">
                      30 <span className="metric_unit">days</span>
                    </span>
                  </div>
                </div>
              </div>
              <div className="vip_prime_card_right">
                <img className="vip_right_vector" src="/images/vip-services-img/vip_cnt_card_vector3.png" alt="vip_right_vector" />
                <div className="right_title_row">
                  <span className="right_title">AGCX Mining Prime</span>
                  <span className="vip_pill_badge">VIP</span>
                </div>
                <div className="right_metric">
                  <span className="right_label">Max Hashrate Fee Discount</span>
                  <span className="right_value"><span className="arrow_down"><i class="ri-arrow-down-line"></i></span> 1%</span>
                </div>
                <button className="join_btn">Join Now</button>
              </div>
            </div>

          </div>
        </div>
      </div> */}


      <div className="vx_benefits">
        <div className="container">
          <h2 className="vx_benefits_heading">Why Become a <span>VIP</span></h2>
          <div className="vx_benefit_grid">
            <div className="vx_benefit vx_benefit_featured vx_benefit_gold">
              <span className="vx_benefit_icon"><img src="/images/vip-services-img/vip_features_icon.svg" alt="" /></span>
              <div className="vx_benefit_body">
                <h3 className="vx_benefit_title">Priority Support</h3>
                <p className="vx_benefit_text">Get faster responses and priority handling from our support team whenever you need help, with personalised guidance as you move up the tiers.</p>
              </div>
            </div>
            <div className="vx_benefit vx_benefit_emerald">
              <span className="vx_benefit_icon"><img src="/images/vip-services-img/vip_features_icon3.svg" alt="" /></span>
              <div className="vx_benefit_body">
                <h3 className="vx_benefit_title">Lower Trading Fees</h3>
                <p className="vx_benefit_text">Reduced maker and taker fees that keep getting better with every tier you unlock.</p>
              </div>
            </div>
            <div className="vx_benefit vx_benefit_purple">
              <span className="vx_benefit_icon"><img src="/images/vip-services-img/vip_features_icon5.svg" alt="" /></span>
              <div className="vx_benefit_body">
                <h3 className="vx_benefit_title">Automatic Upgrades</h3>
                <p className="vx_benefit_text">No application needed. Reach 50K USDT to unlock VIP 1 and climb to VIP 5 at 25M USDT.</p>
              </div>
            </div>
            <div className="vx_benefit vx_benefit_blue">
              <span className="vx_benefit_icon"><img src="/images/vip-services-img/vip_features_icon2.svg" alt="" /></span>
              <div className="vx_benefit_body">
                <h3 className="vx_benefit_title">Rolling 30-Day Volume</h3>
                <p className="vx_benefit_text">Your tier follows your last 30 days of trading, so active traders are always rewarded.</p>
              </div>
            </div>
            <div className="vx_benefit vx_benefit_copper">
              <span className="vx_benefit_icon"><img src="/images/vip-services-img/vip_features_icon7.svg" alt="" /></span>
              <div className="vx_benefit_body">
                <h3 className="vx_benefit_title">Every Market</h3>
                <p className="vx_benefit_text">Spot &amp; margin, Futures and Options each have their own VIP fee schedule.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* <div className="commingsoon">
        <h2>VIP Cards Coming Soon</h2>
      </div> */}

      {/* <div className="vip_cards_section">

        <div className="container">

          <div className="vip_cards_img">
            <img src="/images/vip-services-img/vip_card_img.svg" alt="VIP Cards" />
          </div>

        </div>
      </div> */}

      <Footer />
    </>
  );
};

export default VipServices;